import ExcelJS from 'exceljs';

/**
 * The example workbook for a Chart of Accounts bulk upload.
 *
 * Generated in the browser rather than served as a static asset, so it cannot
 * drift out of step with the parser it documents. The column names and the
 * meaning of each value mirror `parsePtaeCsv` / `parsePtaeName` in the backend's
 * `src/ptae/ptaeController/ptaeImport.mjs` - change one and this must follow.
 *
 * Headers are matched case-insensitively by the parser, and only `Name` is
 * strictly required; the other three are read when present.
 */

const STANFORD_RED = { argb: 'FF8C1515' };

/** Real rows from the East Africa chart of accounts, so the shape is unambiguous. */
const EXAMPLE_ROWS = [
  {
    name: '1160682-002.AABHK.54830.0000.000-OFF CAMPUS WIRELESS PHONE CHGS',
    category: 'Expense',
    active: 'Yes',
    description: 'Seed Communications (non-salary) OFF CAMPUS WIRELESS PHONE CHGS',
  },
  {
    name: '1160682-002.AABHK.54120.0000.000-FOREIGN TRAVEL/LODGING ALLOW',
    category: 'Expense',
    active: 'Yes',
    description: 'Seed Communications (non-salary) FOREIGN TRAVEL/LODGING ALLOW',
  },
  {
    name: '1164993-001.AABHK.54830.0000.000-INTERDEPT ITSS SOFTWARE SALES',
    category: 'Expense',
    active: 'No',
    description: 'Seed Operations Interdept ITSS Software Sales',
  },
];

const INSTRUCTIONS = [
  ['CHART OF ACCOUNTS UPLOAD - HOW THIS FILE IS READ', true],
  ['', false],
  ['Save the Template sheet as .csv, or upload this workbook as-is (.xlsx is accepted).', false],
  ['', false],
  ['COLUMNS', true],
  ['Name         Required. The full chart string plus the expense description.', false],
  ['Category     Optional. Recorded as-is.', false],
  ['Active       Optional. "No" imports the code SUSPENDED rather than skipping it,', false],
  ['             so a closed account stays valid on the claims that already use it', false],
  ['             but cannot be picked for new ones. Anything else means active.', false],
  ['Description  Optional. The funding programme, followed by the expense', false],
  ['             description. Only the programme is kept, as the fund name -', false],
  ['             the description is already carried in the Name column.', false],
  ['', false],
  ['HOW THE NAME COLUMN IS SPLIT', true],
  ['  1160682-002.AABHK.54830.0000.000-OFF CAMPUS WIRELESS PHONE CHGS', false],
  ['  ---┬--- -┬- --┬-- --┬-- ----┬---  --------------┬--------------', false],
  ['  project task award expense  filler              expense description', false],
  ['', false],
  ['The .0000.000 is the same on every row and carries no information, so it is', false],
  ['discarded. The code stored and shown in the app is project-task.award.expense', false],
  ['- for the row above, 1160682-002.AABHK.54830.', false],
  ['', false],
  ['WHAT HAPPENS AFTER UPLOAD', true],
  ['The whole upload is ONE approval request for the finance director, not one', false],
  ['per row. New codes cannot be used on a claim until they approve it.', false],
  ['Re-uploading a corrected report refreshes existing codes in place rather than', false],
  ['duplicating them, so fixing a typo is just another upload.', false],
  ['', false],
  ['A row whose Name does not match the pattern above is reported as skipped', false],
  ['rather than guessed at - a wrongly-split financial code is worse than a', false],
  ['rejected line.', false],
];

/** Build the workbook and hand back a Blob ready to download. */
export async function buildPtaeTemplateBlob(regionLabelText = '') {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Stanford Seed Expense Claim System';

  const sheet = wb.addWorksheet('Template');
  const header = sheet.addRow(['Name', 'Category', 'Active', 'Description']);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: STANFORD_RED };

  for (const row of EXAMPLE_ROWS) {
    sheet.addRow([row.name, row.category, row.active, row.description]);
  }
  [62, 14, 10, 58].forEach((w, i) => { sheet.getColumn(i + 1).width = w; });

  // A note under the examples rather than a 5th column, so deleting the sample
  // rows leaves a clean file the parser will accept.
  const note = sheet.addRow([]);
  note.getCell(1).value =
    'Replace the three example rows above with your own. Keep the header row. '
    + 'See the Instructions sheet for how each column is read.';
  note.getCell(1).font = { italic: true, color: { argb: 'FF666666' } };

  const help = wb.addWorksheet('Instructions');
  if (regionLabelText) {
    const r = help.addRow([`Prepared for: ${regionLabelText}`]);
    r.font = { bold: true, color: STANFORD_RED };
    help.addRow([]);
  }
  for (const [text, bold] of INSTRUCTIONS) {
    const r = help.addRow([text]);
    if (bold) r.font = { bold: true, color: STANFORD_RED };
  }
  help.getColumn(1).width = 92;

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/** Build and save the template. */
export async function downloadPtaeTemplate(regionLabelText = '') {
  const blob = await buildPtaeTemplateBlob(regionLabelText);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'PTAE_Chart_of_Accounts_Template.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

const ptaeTemplate = { buildPtaeTemplateBlob, downloadPtaeTemplate };

export default ptaeTemplate;
